import {
  get,
  post,
  put,
  getDrawProgressData,
  getBudgetReconcileData
} from "../../network/apiService";
import { getBudgetData } from "../budgetReview/budgetReview.action";
import { apiUrlHost } from "../servicerOnboarding/servicerOnboarding.action";
export const getLoanInfoForPL = (loanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/aggregate/loanInfo?toorakLoanId=${loanId}`;
  return get(url);
};

export const updatePurchaseFlags = (requestBody: any) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/flag`;
  return put(url, requestBody);
};

export const updateDrawDocsTags = (documentId: string, requestBody: any[]) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/${documentId}/tags`;
  return put(url, requestBody);
};

export const updateDrawDocsAssets = (requestBody: any[], documentId: any) => {
  let url = `${apiUrlHost}/drawservice/assetmanagement/document/assets`;
  if (!requestBody || requestBody.length === 0) {
    url += `?documentId=${documentId}`;
  }
  return put(url, requestBody);
};

export const updateDocsReviewStatus = (requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/updateStatus`;
  return post(url, requestBody);
};

export const transferDocs = (documentId: string, requestBody: any) => {
  const url = `${apiUrlHost}/documentdetails/${documentId}/transfer`;
  return put(url, requestBody);
};

export const importDocs = (toorakLoanId: string, requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/import`;
  return put(url, requestBody);
};

export const fetchDrawProgress = (toorakLoanId: string, requiredDraw: any) => {
  return async (dispatch: any) => {
    try {
      // Step 1: Call getBudgetData through Redux dispatch to get line items
      let budgetDataResponse: any = null;

      // Create a wrapper promise that resolves when getBudgetData callback is called
      const budgetDataPromise = new Promise<void>((resolve) => {
        const thunkAction = getBudgetData(
          toorakLoanId,
          0,
          (_selectedDocId: any, _budgetLines: any, response: any) => {
            budgetDataResponse = response;
            resolve();
          }
        );
        dispatch(thunkAction);
      });

      await budgetDataPromise;

      // Step 2: Check if budget approval status is "Approved"
      const budgetApprovalStatus = budgetDataResponse?.budgetApprovalStatus;
      if (budgetApprovalStatus !== "Approved") {
        // Only return empty if budget is NOT approved
        return { lineItems: [], draws: [], approved: false };
      }

      // Extract line items from budget data (if available)
      const budgetLineItems =
        budgetDataResponse?.budgetDocumentInfoList?.[0]?.budgetLineItemList || [];

      // Step 3: Extract line items from budget data
      const lineItemsMap = new Map<number, any>();
      budgetLineItems.forEach((item: any) => {
        lineItemsMap.set(item.budgetLineItemId, {
          item: item.item || "",
          description: item.description || "",
          desc: item.description || "",
          budget: item.budget || "",
          financedBudget: item.financedBudget || "",
          inspectionInfo: {}
        });
      });

      // If no line items, return empty line items but with approved flag
      if (budgetLineItems.length === 0) {
        const lineItems = Array.from(lineItemsMap.values());
        return { lineItems, draws: [], approved: true };
      }

      // Step 4: Get all draws and filter out unsuccessful stages
      const allDraws =
        requiredDraw?.allDraws?.filter(
          (draw: any) =>
            draw?.drawNumber !== undefined &&
            draw?.drawNumber !== null &&
            (draw?.stage === "Wire Completed" ||
            draw?.stage === "Wire Unsuccessful" ||
            draw?.stage === "Wire Status Review" ||
            draw?.stage === "Wire Transfer Initiated")
        ) || [];

      if (allDraws.length === 0) {
        const lineItems = Array.from(lineItemsMap.values());
        return { lineItems, draws: [], approved: true };
      }

      // Sort draws by drawNumber
      allDraws.sort(
        (a: any, b: any) => Number(a.drawNumber) - Number(b.drawNumber)
      );

      const qualifyingInspectionStatuses = new Set([
        "Approved",
        "Approved Conditionally",
        "Completed"
      ]);
      const qualifyingDraws = allDraws.filter(
        (draw: any) =>
          draw?.budgetReconcileStatus === "Completed" &&
          qualifyingInspectionStatuses.has(draw?.inspectionReviewStatus)
      );

      // Call budget reconcile endpoint only for qualifying draws
      const drawDataPromises = qualifyingDraws.map((draw: any) =>
        getBudgetReconcileData(draw.drawId).catch((err: any) => {
          console.error(
            `Error fetching budget reconcile for draw ${draw.drawId}:`,
            err
          );
          return { data: [] };
        })
      );

      const qualifyingDrawDataResponses = await Promise.all(drawDataPromises);

      // Create a map of draw data for quick lookup
      const drawDataMap = new Map<string, any>();
      qualifyingDraws.forEach((draw: any, idx: number) => {
        const drawId = draw.drawId.toString();
        drawDataMap.set(drawId, qualifyingDrawDataResponses[idx]?.data || []);
      });

      const processedDraws: any[] = [];

      allDraws.forEach((draw: any, drawIndex: number) => {
        const drawId = draw.drawId.toString();
        const isQualifying = qualifyingDraws.some(
          (d: any) => d.drawId === draw.drawId
        );
        const reconciledItems = isQualifying
          ? drawDataMap.get(drawId) || []
          : [];
        const hasData = reconciledItems.length > 0;

        // Check if the immediately next draw is qualifying (and has data)
        let nextQualifyingDrawData: Map<string, any> | null = null;
        if (!isQualifying && drawIndex + 1 < allDraws.length) {
          const nextDrawId = allDraws[drawIndex + 1].drawId.toString();
          const nextDrawData = drawDataMap.get(nextDrawId);
          if (nextDrawData && nextDrawData.length > 0) {
            nextQualifyingDrawData = new Map();
            nextDrawData.forEach((item: any) => {
              nextQualifyingDrawData!.set(
                item.budgetLineItem.item.trim(),
                item
              );
            });
          }
        }

        // Find the previous qualifying draw (if this is non-qualifying)
        let previousQualifyingDrawData: Map<string, any> | null = null;
        if (!isQualifying && drawIndex > 0) {
          for (let i = drawIndex - 1; i >= 0; i--) {
            const prevDrawId = allDraws[i].drawId.toString();
            const prevDrawData = drawDataMap.get(prevDrawId);
            if (prevDrawData && prevDrawData.length > 0) {
              previousQualifyingDrawData = new Map();
              prevDrawData.forEach((item: any) => {
                previousQualifyingDrawData!.set(
                  item.budgetLineItem.item.trim(),
                  item
                );
              });
              break;
            }
          }
        }

        processedDraws.push({
          drawId,
          lineItems: Array.from(lineItemsMap.values()).map((lineItem: any) => {
            // Match by item name
            const matchingItem = hasData
              ? reconciledItems.find(
                  (item: any) =>
                    item.budgetLineItem.item.trim() === lineItem.item.trim()
                )
              : null;

            // For qualifying draws with data
            if (isQualifying && hasData && matchingItem) {
              const financedBudget =
                Number(matchingItem.budgetLineItem?.financedBudget) || 0;
              const approvedLatestCompletion =
                Number(matchingItem.approvedLatestCompletion) || 0;
              const previousCompletion =
                Number(matchingItem.previousCompletion) || 0;

              // Calculate approved draw amount using the formula:
              const approvedDrawAmount = (
                (financedBudget *
                  (approvedLatestCompletion - previousCompletion)) /
                100
              ).toFixed(2);

              return {
                item: lineItem.item,
                desc: lineItem.desc,
                amount: approvedDrawAmount,
                completion: approvedLatestCompletion.toFixed(2)
              };
            }

            // Case 1: First draw (non-qualifying) with next qualifying draw
            if (drawIndex === 0 && !isQualifying && nextQualifyingDrawData) {
              const nextDrawItem = nextQualifyingDrawData.get(
                lineItem.item.trim()
              );
              if (nextDrawItem) {
                const financedBudget =
                  Number(nextDrawItem.budgetLineItem?.financedBudget) || 0;
                const nextDrawLatestCompletion =
                  Number(nextDrawItem.previousCompletion) || 0;

                // For first draw: previousCompletion = 0, latestCompletion = nextDraw.previousCompletion
                const amount = (
                  (financedBudget * nextDrawLatestCompletion) /
                  100
                ).toFixed(2);

                return {
                  item: lineItem.item,
                  desc: lineItem.desc,
                  amount: amount,
                  completion: nextDrawLatestCompletion.toFixed(2)
                };
              }
            }

            // Case 2: Non-first draw where previous is qualifying and next is qualifying
            if (
              drawIndex > 0 &&
              !isQualifying &&
              previousQualifyingDrawData &&
              nextQualifyingDrawData
            ) {
              const nextDrawItem = nextQualifyingDrawData.get(
                lineItem.item.trim()
              );
              const prevDrawItem = previousQualifyingDrawData.get(
                lineItem.item.trim()
              );

              if (nextDrawItem && prevDrawItem) {
                const financedBudget =
                  Number(nextDrawItem.budgetLineItem?.financedBudget) || 0;
                const nextDrawLatestCompletion =
                  Number(nextDrawItem.previousCompletion) || 0;
                const prevDrawLatestCompletion =
                  Number(prevDrawItem.approvedLatestCompletion) || 0;

                // For this draw: previousCompletion = prevDraw.approvedLatestCompletion, latestCompletion = nextDraw.previousCompletion
                const amount = (
                  (financedBudget *
                    (nextDrawLatestCompletion - prevDrawLatestCompletion)) /
                  100
                ).toFixed(2);

                return {
                  item: lineItem.item,
                  desc: lineItem.desc,
                  amount: amount,
                  completion: nextDrawLatestCompletion.toFixed(2)
                };
              }
            }

            // Fallback: show "-|-" for all other cases
            return {
              item: lineItem.item,
              desc: lineItem.desc,
              amount: "-",
              completion: "-"
            };
          })
        });

        const isDerivedFromAdjacent =
          !isQualifying &&
          ((drawIndex === 0 && !!nextQualifyingDrawData) ||
            (drawIndex > 0 &&
              !!previousQualifyingDrawData &&
              !!nextQualifyingDrawData));

        processedDraws[processedDraws.length - 1].isDerivedFromAdjacent =
          isDerivedFromAdjacent;

        // Update inspection info for line items
        reconciledItems.forEach((item: any) => {
          const lineItemId = item.budgetLineItemsId;
          if (lineItemsMap.has(lineItemId)) {
            const lineItem = lineItemsMap.get(lineItemId)!;
            lineItem.inspectionInfo[drawId] = {
              latestCompletion:
                isQualifying && hasData ? item.approvedLatestCompletion : "-",
              latestInspectionBudget: item.budgetLineItem?.financedBudget,
              previousCompletion: item.previousCompletion
            };
          }
        });
      });

      const lineItems = Array.from(lineItemsMap.values());
      return { lineItems, draws: processedDraws, approved: true };
    } catch (err) {
      console.error(err);
      return { lineItems: [], draws: [], approved: false };
    }
  };
};

export const startTaggingDocs = (toorakLoanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/tag`;
  return put(url);
};
